import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import MetricCard from '../dashboard/components/MetricCard';
import PerformanceChart from './components/PerformanceChart';
import OptimalTimingHeatmap from './components/OptimalTimingHeatmap';
import AISuggestionCard from './components/AISuggestionCard';
import ContentTypeChart from './components/ContentTypeChart';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const InsightLabAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const { user, subscription } = useAuth();
  const isFree = subscription?.tier !== 'pro';
  const [dateRange, setDateRange] = useState('7days');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState({
    totalPosts: 0,
    totalImpressions: 0,
    engagementRate: 0,
    followerGrowth: 0
  });

  useEffect(() => {
    if (user) {
      fetchAnalyticsData();
    }
  }, [user, dateRange]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      
      // Calculate date range
      const now = new Date();
      let startDate = new Date();
      
      switch (dateRange) {
        case '7days':
          startDate?.setDate(now?.getDate() - 7);
          break;
        case '30days':
          startDate?.setDate(now?.getDate() - 30);
          break;
        case '90days':
          startDate?.setDate(now?.getDate() - 90);
          break;
        default:
          startDate?.setDate(now?.getDate() - 7);
      }

      // Fetch content posts from database
      const { data: posts, error } = await supabase?.from('content_posts')?.select('*')?.eq('user_id', user?.id)?.gte('created_at', startDate?.toISOString())?.order('created_at', { ascending: false });

      if (error) throw error;

      // Calculate real metrics from posts
      const totalPosts = posts?.length || 0;
      
      // Since we don't have actual engagement data yet, show 0 or calculated estimates
      setAnalyticsData({
        totalPosts,
        totalImpressions: 0,
        engagementRate: 0,
        followerGrowth: 0
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const dateRangeOptions = [
    { value: '7days', label: 'Last 7 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: '90days', label: 'Last 90 Days' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const platformOptions = [
    { value: 'all', label: 'All Platforms' },
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'twitter', label: 'Twitter' }
  ];

  const performanceMetrics = [
    {
      id: 1,
      title: 'Total Posts',
      value: analyticsData?.totalPosts?.toString(),
      change: '+0%',
      changeType: 'neutral',
      trend: 'vs last period',
      icon: 'FileText',
      iconColor: 'var(--color-primary)'
    },
    {
      id: 2,
      title: 'Total Impressions',
      value: analyticsData?.totalImpressions?.toLocaleString(),
      change: '+0%',
      changeType: 'neutral',
      trend: 'vs last period',
      icon: 'Eye',
      iconColor: 'var(--color-success)'
    },
    {
      id: 3,
      title: 'Engagement Rate',
      value: `${analyticsData?.engagementRate}%`,
      change: '+0%',
      changeType: 'neutral',
      trend: 'vs last period',
      icon: 'TrendingUp',
      iconColor: 'var(--color-accent)'
    },
    {
      id: 4,
      title: 'Follower Growth',
      value: `+${analyticsData?.followerGrowth}`,
      change: '+0%',
      changeType: 'neutral',
      trend: 'this period',
      icon: 'Users',
      iconColor: 'var(--color-warning)'
    }
  ];

  const aiSuggestions = [
    {
      id: 1,
      type: 'high-performing',
      title: 'React Performance Tips',
      description: 'Your posts about React optimization get 3x more engagement. Consider creating a deep-dive series.',
      confidence: 92,
      suggestedTopics: ['React Server Components', 'Memoization Patterns', 'Bundle Size Optimization'],
      icon: 'Sparkles'
    },
    {
      id: 2,
      type: 'trending',
      title: 'AI & Developer Tools',
      description: 'Industry trending: AI-powered development tools. Your audience shows high interest in this topic.',
      confidence: 87,
      suggestedTopics: ['GitHub Copilot', 'AI Code Review', 'ChatGPT for Developers'],
      icon: 'TrendingUp'
    },
    {
      id: 3,
      type: 'timing',
      title: 'Optimal Posting Window',
      description: 'Posts published Tuesday-Thursday 9-11 AM get 45% more engagement from your audience.',
      confidence: 95,
      suggestedTopics: ['Schedule content during peak hours', 'Batch content creation'],
      icon: 'Clock'
    },
    {
      id: 4,
      type: 'format',
      title: 'Code Snippet Posts',
      description: 'Posts with code snippets receive 2.5x more saves and shares. Include more technical examples.',
      confidence: 89,
      suggestedTopics: ['TypeScript Tips', 'CSS Tricks', 'JavaScript Patterns'],
      icon: 'Code'
    }
  ];

  const trendingAlerts = [
    {
      id: 1,
      type: 'viral',
      title: 'Viral Content Opportunity',
      message: 'Your post about "TypeScript strict mode" is trending. Create follow-up content.',
      timestamp: '2 hours ago',
      action: 'Create Follow-up'
    },
    {
      id: 2,
      type: 'hashtag',
      title: '#WebPerformance Trending',
      message: 'This hashtag is gaining traction in your network. Consider using it in your next post.',
      timestamp: '5 hours ago',
      action: 'Use Hashtag'
    },
    {
      id: 3,
      type: 'competitor',
      title: 'Industry Leader Posted',
      message: 'Kent C. Dodds shared insights on React testing. Engage or create complementary content.',
      timestamp: '1 day ago',
      action: 'View Post'
    }
  ];

  const handleExportData = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      alert('Analytics data exported successfully!');
    }, 1500);
  };

  const handleScheduleSuggested = (suggestion) => {
    navigate('/idea-refiner', { state: { suggestedTopic: suggestion?.title } });
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-6 lg:p-8 relative">
      {/* Pro-only overlay for free users */}
      {isFree && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-background/60 backdrop-blur-sm rounded-xl">
          <div className="text-center p-8 max-w-md">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Icon name="Lock" size={28} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Analytics is a Pro Feature</h2>
            <p className="text-muted-foreground mb-6">
              Upgrade to Pro to unlock detailed post analytics, engagement tracking, and AI-powered content insights.
            </p>
            <Button onClick={() => navigate('/pricing')} className="bg-primary hover:bg-primary/90 text-white px-8">
              <Icon name="Zap" size={16} className="mr-2" /> Upgrade to Pro
            </Button>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
              Insight Lab Analytics
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Comprehensive performance analysis and AI-powered content optimization
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Select
              value={dateRange}
              onChange={setDateRange}
              options={dateRangeOptions}
              className="w-full md:w-40"
            />
            <Select
              value={platformFilter}
              onChange={setPlatformFilter}
              options={platformOptions}
              className="w-full md:w-40"
            />
            <Button
              variant="outline"
              iconName="Download"
              onClick={handleExportData}
              loading={loading}
              className="w-full md:w-auto"
            >
              Export Data
            </Button>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
        {performanceMetrics?.map((metric) => (
          <MetricCard key={metric?.id} {...metric} />
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
        {/* Left Column - Charts */}
        <div className="lg:col-span-2 space-y-6">
          {/* Performance Chart */}
          <div className="bg-card rounded-lg p-4 md:p-6 shadow-sm border border-border">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg md:text-xl font-semibold text-foreground">
                Posting Frequency & Engagement
              </h2>
              <Button variant="ghost" size="sm" iconName="Info">
                Details
              </Button>
            </div>
            <PerformanceChart platformFilter={platformFilter} />
          </div>

          {/* Optimal Timing Heatmap */}
          <div className="bg-card rounded-lg p-4 md:p-6 shadow-sm border border-border">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg md:text-xl font-semibold text-foreground">
                Optimal Posting Times
              </h2>
              <Button variant="ghost" size="sm" iconName="Calendar">
                Schedule
              </Button>
            </div>
            <OptimalTimingHeatmap />
          </div>

          {/* Content Type Performance */}
          <div className="bg-card rounded-lg p-4 md:p-6 shadow-sm border border-border">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg md:text-xl font-semibold text-foreground">
                Content Type Performance
              </h2>
              <Button variant="ghost" size="sm" iconName="BarChart3">
                View All
              </Button>
            </div>
            <ContentTypeChart />
          </div>
        </div>

        {/* Right Column - AI Suggestions & Alerts */}
        <div className="space-y-6">
          {/* AI-Powered Suggestions */}
          <div className="bg-card rounded-lg p-4 md:p-6 shadow-sm border border-border">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                <Icon name="Sparkles" size={18} color="var(--color-primary)" />
              </div>
              <h2 className="text-lg md:text-xl font-semibold text-foreground">
                AI Content Suggestions
              </h2>
            </div>
            <div className="space-y-3">
              {aiSuggestions?.map((suggestion) => (
                <AISuggestionCard
                  key={suggestion?.id}
                  suggestion={suggestion}
                  onSchedule={() => handleScheduleSuggested(suggestion)}
                />
              ))}
            </div>
          </div>

          {/* Trending Alerts */}
          <div className="bg-card rounded-lg p-4 md:p-6 shadow-sm border border-border">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-accent/20 flex items-center justify-center">
                <Icon name="Bell" size={18} color="var(--color-accent)" />
              </div>
              <h2 className="text-lg md:text-xl font-semibold text-foreground">
                Trending Alerts
              </h2>
            </div>
            <div className="space-y-3">
              {trendingAlerts?.map((alert) => (
                <div
                  key={alert?.id}
                  className="p-3 rounded-lg border border-border hover:border-primary/30 transition-all"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h4 className="text-sm font-semibold text-foreground">{alert?.title}</h4>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {alert?.timestamp}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">{alert?.message}</p>
                  <Button variant="outline" size="xs" className="w-full">
                    {alert?.action}
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-card rounded-lg p-4 md:p-6 shadow-sm border border-border">
            <h3 className="text-base font-semibold text-foreground mb-3">
              Quick Actions
            </h3>
            <div className="space-y-2">
              <Button
                variant="outline"
                iconName="FileText"
                className="w-full justify-start"
                onClick={() => navigate('/idea-refiner')}
              >
                Generate Content Report
              </Button>
              <Button
                variant="outline"
                iconName="Target"
                className="w-full justify-start"
              >
                Set Performance Goals
              </Button>
              <Button
                variant="outline"
                iconName="Calendar"
                className="w-full justify-start"
                onClick={() => navigate('/schedule')}
              >
                Schedule Suggested Content
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsightLabAnalyticsDashboard;